import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { getProductImageAlt, deriveSlugFromName } from '@/lib/imageAltTexts';
import { optimizeCloudinaryUrl } from '@/lib/cloudinaryUpload';

interface CardTemplatePreviewProps {
  frontImage: string;
  backImage: string;
  name: string;
  slug?: string;
  className?: string;
  priority?: boolean;
}

export const CardTemplatePreview: React.FC<CardTemplatePreviewProps> = ({
  frontImage,
  backImage,
  name,
  slug,
  className,
  priority = false,
}) => {
  const [frontLoaded, setFrontLoaded] = useState(false);
  const [backLoaded, setBackLoaded] = useState(false);
  const [frontError, setFrontError] = useState(false);
  const [backError, setBackError] = useState(false);
  const { language } = useLanguage();

  // Responsive Cloudinary width: smaller images on mobile saves ~330 KiB
  const cloudinaryWidth = typeof window !== 'undefined' && window.innerWidth < 768 ? 400 : 640;

  const templateSlug = slug || deriveSlugFromName(name);
  const frontAlt = getProductImageAlt(templateSlug, 'front', language as any);
  const backAlt = getProductImageAlt(templateSlug, 'back', language as any);

  React.useEffect(() => {
    if (backImage && !backError) {
      const img = new Image();
      img.src = backImage;
      img.onload = () => setBackLoaded(true);
      img.onerror = () => setBackError(true);
    }
  }, [backImage, backError]);

  return (
    <div className={cn("card-flip-container w-full h-full relative", className)}>
      <div className="card-flip-inner w-full h-full absolute inset-0">
        <div className="card-face">
          {!frontLoaded && !frontError && (
            <div className="w-full h-full bg-secondary animate-pulse" />
          )}
          <img
            src={frontError ? '/placeholder.svg' : optimizeCloudinaryUrl(frontImage, cloudinaryWidth)}
            alt={frontAlt}
            className={cn(
              "w-full h-full object-cover transition-opacity duration-300",
              frontLoaded ? "opacity-100" : "opacity-0"
            )}
            loading={priority ? "eager" : "lazy"}
            fetchPriority={priority ? "high" : "auto"}
            decoding={priority ? "sync" : "async"}
            onLoad={() => setFrontLoaded(true)}
            onError={() => {
              setFrontError(true);
              setFrontLoaded(true);
            }}
          />
        </div>
        <div className="card-face card-back">
          {!backLoaded && !backError && (
            <div className="w-full h-full bg-secondary animate-pulse" />
          )}
          <img
            src={backError ? '/placeholder.svg' : optimizeCloudinaryUrl(backImage, cloudinaryWidth)}
            alt={backAlt}
            className={cn(
              "w-full h-full object-cover",
              backLoaded ? "opacity-100" : "opacity-0"
            )}
            loading="eager"
            fetchPriority={priority ? "high" : "auto"}
            decoding="async"
          />
        </div>
      </div>
    </div>
  );
};
